import { apiClient } from './apiClient';
import { indexedDbService } from './indexedDbService';
import { offlineService } from './offlineService';
import { UserDataFieldDto } from '../types/customUserDataFields';

export interface Job {
    id: string;  // Guid from backend
    jobNumber: string;
    customerId: string;  // Guid from backend
    customerName: string;
    customerEmail: string;
    customerPhone: string;
    assignedSalesPersonId: string;
    assignedSalesPersonName: string;
    jobAddress: string;
    statusId: number;
    statusName: string;
    statusColor: string;
    type: JobType;
    estimatedValue: number;
    actualValue?: number;
    priority: Priority;
    createdDate: string;
    expectedCompletionDate?: string;
    actualCompletionDate?: string;
    lastStatusChangeDate?: string;
    lastStatusChangedByName: string;
    notes: string;
    appointmentCount: number;
    documentCount: number;
    communicationCount: number;
    // Populated only by the single-job endpoint (GetJobByIdAsync) so PDF header
    // placeholders can render the assigned salesperson's {{CODE}} values without
    // an extra auth-gated round-trip.
    assignedSalesPersonUserDataFields?: UserDataFieldDto[];
}

export interface CreateJobDto {
    customerId: string;  // Guid from backend
    assignedSalesPersonId: string;
    jobAddress?: string;
    type: JobType;
    estimatedValue: number;
    actualValue?: number;
    priority?: Priority;
    expectedCompletionDate?: string;
    notes?: string;
}

export interface UpdateJobDto {
    id: string;  // Guid from backend
    customerId: string;  // Guid from backend
    assignedSalesPersonId: string;
    jobAddress?: string;
    type: JobType;
    estimatedValue: number;
    actualValue?: number;
    priority?: Priority;
    expectedCompletionDate?: string;
    notes?: string;
}

export interface ChangeJobStatusDto {
    jobId: string;  // Guid from backend
    newStatusId: number;
    reason?: string;
    notes?: string;
}

export interface JobSearchDto {
    searchTerm?: string;
    assignedSalesPersonId?: string;
    statusId?: number;
    types?: JobType[]; // Multi-select type filter
    priorities?: Priority[]; // Multi-select priority filter
    customerIds?: string[]; // Multi-select customer filter (Guids)
    createdFrom?: string;
    createdTo?: string;
    page?: number;
    pageSize?: number;
}

export interface CreateJobFromAppointmentDto {
    appointmentId: string;
    customerId: string;  // Guid from backend
    assignedSalesPersonId: string;
    jobAddress?: string;
    type: JobType;
    estimatedValue: number;
    priority?: Priority;
    expectedCompletionDate?: string;
    notes?: string;
}

export interface DuplicateJobDto {
    sourceJobId: string;  // Guid from backend
    jobAddress?: string;
}

export enum JobType {
    KitchenDesign = 1,
    KitchenRemodel = 2,
    NewKitchen = 3,
    Consultation = 4,
    Renovation = 5,
    Other = 6
}

export enum Priority {
    Low = 1,
    Medium = 2,
    High = 3,
    Urgent = 4
}

class JobService {
    private static instance: JobService;
    private baseUrl = '/Jobs';

    public static getInstance(): JobService {
        if (!JobService.instance) {
            JobService.instance = new JobService();
        }
        return JobService.instance;
    }

    async getJobs(searchDto?: JobSearchDto): Promise<Job[]> {
        try {
            // Check if we're offline
            const isOffline = !offlineService.getOnlineStatus();
            
            if (isOffline) {
                console.log('Offline mode: Getting jobs from IndexedDB');
                // Get current user to determine business ID
                const currentUser = await indexedDbService.getCurrentUser();
                if (!currentUser) {
                    console.error('No current user found in offline mode');
                    return [];
                }
                
                const jobs = await indexedDbService.getJobsForBusiness(currentUser.businessId);
                
                // Apply basic filtering if search criteria provided
                if (searchDto) {
                    let filteredJobs = jobs;
                    
                    if (searchDto.searchTerm) {
                        const searchTerm = searchDto.searchTerm.toLowerCase();
                        filteredJobs = filteredJobs.filter(job => 
                            job.jobNumber?.toLowerCase().includes(searchTerm) ||
                            job.customerName?.toLowerCase().includes(searchTerm) ||
                            job.jobAddress?.toLowerCase().includes(searchTerm) ||
                            job.notes?.toLowerCase().includes(searchTerm)
                        );
                    }
                    
                    if (searchDto.customerIds && searchDto.customerIds.length > 0) {
                        filteredJobs = filteredJobs.filter(job => 
                            searchDto.customerIds!.includes(job.customerId)
                        );
                    }
                    
                    if (searchDto.statusId) {
                        filteredJobs = filteredJobs.filter(job => job.statusId === searchDto.statusId);
                    }
                    
                    return filteredJobs;
                }
                
                return jobs;
            }
            
            if (searchDto && ((searchDto.types && searchDto.types.length > 0) || (searchDto.priorities && searchDto.priorities.length > 0) || (searchDto.customerIds && searchDto.customerIds.length > 0))) {
                // Use POST for complex search with arrays
                const response = await apiClient.post<Job[]>(`${this.baseUrl}/search`, searchDto);
                return response.data;
            } else {
                // Use GET for simple searches
                const params = new URLSearchParams();
                if (searchDto) {
                    Object.entries(searchDto).forEach(([key, value]) => {
                        if (value !== undefined && value !== null && value !== '' && value !== 0) {
                            if (Array.isArray(value)) {
                                // Handle arrays by adding multiple parameters with the same key
                                value.forEach(item => {
                                    params.append(key, item.toString());
                                });
                            } else {
                                params.append(key, value.toString());
                            }
                        }
                    });
                }

                const response = await apiClient.get<Job[]>(`${this.baseUrl}?${params.toString()}`);
                return response.data;
            }
        } catch (error: any) {
            console.error('Error fetching jobs:', error);
            throw error;
        }
    }

    async getJob(id: string): Promise<Job> {
        try {
            // Check if we're offline
            const isOffline = !offlineService.getOnlineStatus();
            
            if (isOffline) {
                console.log('Offline mode: Getting job from IndexedDB');
                const job = await indexedDbService.getJob(id);
                if (job) {
                    return job;
                } else {
                    throw new Error(`Job ${id} not found in offline storage`);
                }
            }
            
            const response = await apiClient.get<Job>(`${this.baseUrl}/${id}`);
            return response.data;
        } catch (error: any) {
            console.error('Error fetching job:', error);
            throw error;
        }
    }

    async getJobByNumber(jobNumber: string): Promise<Job> {
        try {
            const response = await apiClient.get<Job>(`${this.baseUrl}/by-number/${jobNumber}`);
            return response.data;
        } catch (error: any) {
            console.error('Error fetching job by number:', error);
            throw error;
        }
    }

    async getJobsBySalesPerson(salesPersonId: string): Promise<Job[]> {
        try {
            const response = await apiClient.get<Job[]>(`${this.baseUrl}/by-sales-person/${salesPersonId}`);
            return response.data;
        } catch (error: any) {
            console.error('Error fetching jobs by sales person:', error);
            throw error;
        }
    }

    async getJobsByStatus(statusId: number): Promise<Job[]> {
        try {
            const response = await apiClient.get<Job[]>(`${this.baseUrl}/by-status/${statusId}`);
            return response.data;
        } catch (error: any) {
            console.error('Error fetching jobs by status:', error);
            throw error;
        }
    }

    async getJobsByCustomer(customerId: string): Promise<Job[]> {
        try {
            const response = await apiClient.get<Job[]>(`${this.baseUrl}/by-customer/${customerId}`);
            return response.data;
        } catch (error: any) {
            console.error('Error fetching jobs by customer:', error);
            throw error;
        }
    }

    async createJob(job: CreateJobDto): Promise<Job> {
        try {
            const response = await apiClient.post<Job>(this.baseUrl, job);
            return response.data;
        } catch (error: any) {
            console.error('Error creating job:', error);
            throw error;
        }
    }

    async updateJob(job: UpdateJobDto): Promise<Job> {
        try {
            const response = await apiClient.put<Job>(`${this.baseUrl}/${job.id}`, job);
            return response.data;
        } catch (error: any) {
            console.error('Error updating job:', error);
            throw error;
        }
    }

    async changeJobStatus(changeStatusDto: ChangeJobStatusDto): Promise<void> {
        try {
            await apiClient.put(`${this.baseUrl}/${changeStatusDto.jobId}/status`, changeStatusDto);
        } catch (error: any) {
            console.error('Error changing job status:', error);
            throw error;
        }
    }

    async deleteJob(id: string): Promise<void> {
        try {
            await apiClient.delete(`${this.baseUrl}/${id}`);
        } catch (error: any) {
            console.error('Error deleting job:', error);
            throw error;
        }
    }

    async createJobFromAppointment(dto: CreateJobFromAppointmentDto): Promise<Job> {
        try {
            const response = await apiClient.post<Job>(`${this.baseUrl}/from-appointment`, dto);
            return response.data;
        } catch (error: any) {
            console.error('Error creating job from appointment:', error);
            throw error;
        }
    }

    async duplicateJob(dto: DuplicateJobDto): Promise<Job> {
        try {
            const response = await apiClient.post<Job>(`${this.baseUrl}/${dto.sourceJobId}/duplicate`, dto);
            return response.data;
        } catch (error: any) {
            console.error('Error duplicating job:', error);
            throw error;
        }
    }

    // Helper methods for display
    getJobTypeDisplayName(type: JobType | string | number): string {
        // Handle string enum names
        if (typeof type === 'string') {
            const stringToDisplayName: { [key: string]: string } = {
                'KitchenDesign': 'Kitchen Design',
                'KitchenRemodel': 'Kitchen Remodel',
                'NewKitchen': 'New Kitchen',
                'Consultation': 'Consultation',
                'Renovation': 'Renovation',
                'Other': 'Other'
            };
            return stringToDisplayName[type] || 'Unknown';
        }
        
        // Convert number to JobType enum if needed
        let jobType: JobType;
        if (typeof type === 'number') {
            jobType = type as JobType;
        } else {
            jobType = type;
        }
        
        const typeNames = {
            [JobType.KitchenDesign]: 'Kitchen Design',
            [JobType.KitchenRemodel]: 'Kitchen Remodel',
            [JobType.NewKitchen]: 'New Kitchen',
            [JobType.Consultation]: 'Consultation',
            [JobType.Renovation]: 'Renovation',
            [JobType.Other]: 'Other'
        };
        return typeNames[jobType] || 'Unknown';
    }

    getPriorityDisplayName(priority: Priority | string | number): string {
        // Handle string enum names
        if (typeof priority === 'string') {
            const stringToDisplayName: { [key: string]: string } = {
                'Low': 'Low',
                'Medium': 'Medium',
                'High': 'High',
                'Urgent': 'Urgent'
            };
            return stringToDisplayName[priority] || 'Unknown';
        }
        
        // Convert number to Priority enum if needed
        let priorityEnum: Priority;
        if (typeof priority === 'number') {
            priorityEnum = priority as Priority;
        } else {
            priorityEnum = priority;
        }
        
        const priorityNames = {
            [Priority.Low]: 'Low',
            [Priority.Medium]: 'Medium',
            [Priority.High]: 'High',
            [Priority.Urgent]: 'Urgent'
        };
        return priorityNames[priorityEnum] || 'Unknown';
    }

    getPriorityColor(priority: Priority | string | number): string {
        // Handle string enum names
        if (typeof priority === 'string') {
            const stringToColor: { [key: string]: string } = {
                'Low': '#4CAF50',
                'Medium': '#FF9800',
                'High': '#F44336',
                'Urgent': '#9C27B0'
            };
            return stringToColor[priority] || '#757575';
        }
        
        // Convert number to Priority enum if needed
        let priorityEnum: Priority;
        if (typeof priority === 'number') {
            priorityEnum = priority as Priority;
        } else {
            priorityEnum = priority;
        }
        
        const priorityColors = {
            [Priority.Low]: '#4CAF50',
            [Priority.Medium]: '#FF9800',
            [Priority.High]: '#F44336',
            [Priority.Urgent]: '#9C27B0'
        };
        return priorityColors[priorityEnum] || '#757575';
    }

    formatCurrency(amount: number): string {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    }
}

export const jobService = JobService.getInstance();
